import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, User, Mail, Lock, Phone, MapPin, Globe, Briefcase } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import { API_CONFIG } from '../../config/api.config';

interface Country {
  countryid: number;
  countryname: string;
  countrycode: string;
}

interface State {
  stateid: number;
  statename: string;
  statecode: string;
}

interface City {
  cityid: number;
  cityname: string;
}

interface BusinessType {
  businesstypeid: number;
  businesstype: string;
}

interface SignupFormData {
  company_name: string;
  company_code_prefix: string;
  phone: string;
  address1: string;
  address2: string;
  country: number;
  state: number;
  city: number;
  postal_code: string;
  email: string;
  business_type: number;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
}

// Rate limiting - allow 3 signup attempts per 15 minutes
const RATE_LIMIT_KEY = 'signup_attempts';
const RATE_LIMIT_MAX = 3;
const RATE_LIMIT_WINDOW = 15 * 60 * 1000; // 15 minutes in milliseconds

interface RateLimitData {
  attempts: number;
  firstAttempt: number;
}

const getRateLimitData = (): RateLimitData => {
  const data = localStorage.getItem(RATE_LIMIT_KEY);
  if (!data) return { attempts: 0, firstAttempt: Date.now() };
  return JSON.parse(data);
};

const setRateLimitData = (data: RateLimitData): void => {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
};

const checkRateLimit = (): { allowed: boolean; remaining: number; resetIn: number } => {
  const data = getRateLimitData();
  const now = Date.now();
  const timeSinceFirst = now - data.firstAttempt;

  // Reset if window has passed
  if (timeSinceFirst > RATE_LIMIT_WINDOW) {
    setRateLimitData({ attempts: 0, firstAttempt: now });
    return { allowed: true, remaining: RATE_LIMIT_MAX, resetIn: 0 };
  }

  // Check if limit exceeded
  if (data.attempts >= RATE_LIMIT_MAX) {
    const resetIn = Math.ceil((RATE_LIMIT_WINDOW - timeSinceFirst) / 1000 / 60); // minutes
    return { allowed: false, remaining: 0, resetIn };
  }

  return { allowed: true, remaining: RATE_LIMIT_MAX - data.attempts, resetIn: 0 };
};

const incrementRateLimit = (): void => {
  const data = getRateLimitData();
  setRateLimitData({ ...data, attempts: data.attempts + 1 });
};

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<SignupFormData>({
    company_name: '',
    company_code_prefix: '',
    phone: '',
    address1: '',
    address2: '',
    country: 0,
    state: 0,
    city: 0,
    postal_code: '',
    email: '',
    business_type: 0,
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);

  // Load countries and business types on mount
  useEffect(() => {
    loadCountries();
    loadBusinessTypes();
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (formData.country) {
      loadStates(formData.country);
      setFormData(prev => ({ ...prev, state: 0, city: 0 }));
    }
  }, [formData.country]);

  // Load cities when state changes
  useEffect(() => {
    if (formData.state) {
      loadCities(formData.state);
      setFormData(prev => ({ ...prev, city: 0 }));
    }
  }, [formData.state]);

  const loadCountries = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COUNTRIES}`);
      const data = await response.json();
      setCountries(data.items || []);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadStates = async (countryId: number) => {
    setLoadingGeo(true);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STATES}?country_id=${countryId}`);
      const data = await response.json();
      setStates(data.items || []);
    } catch (err) {
      console.error('Failed to load states:', err);
    } finally {
      setLoadingGeo(false);
    }
  };

  const loadCities = async (stateId: number) => {
    setLoadingGeo(true);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CITIES}?state_id=${stateId}`);
      const data = await response.json();
      setCities(data.items || []);
    } catch (err) {
      console.error('Failed to load cities:', err);
    } finally {
      setLoadingGeo(false);
    }
  };

  const loadBusinessTypes = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PUBLIC_DROPDOWN}/businesstype`);
      const data = await response.json();
      setBusinessTypes(data.items || []);
    } catch (err) {
      console.error('Failed to load business types:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['country', 'state', 'city', 'business_type'].includes(name) 
        ? parseInt(value) || 0 
        : value,
    }));
    setError(null);
    setSuccess(null);
  };

  const validateForm = (): string | null => {
    // Required fields
    if (!formData.company_name) return 'Company name is required';
    if (!formData.first_name) return 'First name is required';
    if (!formData.last_name) return 'Last name is required';
    if (!formData.email) return 'Email is required';
    if (!formData.password) return 'Password is required';
    if (!formData.country) return 'Country is required';
    if (!formData.state) return 'State/Province is required';
    if (!formData.city) return 'City is required';
    if (!formData.business_type) return 'Business type is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address';
    }

    // Password validation
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }

    if (formData.password !== formData.confirm_password) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Check rate limit
    const rateLimit = checkRateLimit();
    if (!rateLimit.allowed) {
      setError(`Too many signup attempts. Please try again in ${rateLimit.resetIn} minutes.`);
      return;
    }

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      // Increment rate limit counter
      incrementRateLimit();

      // Prepare registration data
      const registrationData = {
        parent_company: null,
        company_name: formData.company_name,
        company_code_prefix: formData.company_code_prefix || null,
        phone: formData.phone || null,
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        postal_code: formData.postal_code || null,
        email: formData.email,
        business_type: formData.business_type,
        sub_business: null,
        plan_type: true, // Small Business
        valid_till: null,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password_hash: formData.password, // Backend will hash it
        secondary_email: null,
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Registration failed');
      }

      setSuccess('Account created successfully! Redirecting to login...');
      
      // Clear form
      setFormData({
        company_name: '',
        company_code_prefix: '',
        phone: '',
        address1: '',
        address2: '',
        country: 0,
        state: 0,
        city: 0,
        postal_code: '',
        email: '',
        business_type: 0,
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <img 
              src="/login-logo.png" 
              alt="SecureView Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">Create Your Account</h1>
          <p className="text-text-muted">Join SecureView - Smart Security Solutions</p>
        </div>

        {/* Signup card */}
        <div className="bg-surface-primary rounded-card shadow-card border border-surface-secondary p-8">
          {error && (
            <div className="mb-6">
              <Alert variant="error" message={error} onClose={() => setError(null)} />
            </div>
          )}

          {success && (
            <div className="mb-6">
              <Alert variant="success" message={success} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Building2 size={20} />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="company_name"
                  type="text"
                  label="Company Name *"
                  placeholder="Your Company Name"
                  value={formData.company_name}
                  onChange={handleChange}
                  leftIcon={<Building2 size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                />
                <Input
                  name="company_code_prefix"
                  type="text"
                  label="Company Code (Optional)"
                  placeholder="e.g., ABC"
                  value={formData.company_code_prefix}
                  onChange={handleChange}
                  fullWidth
                  disabled={isLoading}
                  maxLength={10}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  name="phone"
                  type="tel"
                  label="Phone (Optional)"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  leftIcon={<Phone size={18} />}
                  fullWidth
                  disabled={isLoading}
                />
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Briefcase size={16} />
                      Business Type *
                    </span>
                  </label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className="w-full px-3 py-2 bg-bg-secondary border border-surface-secondary rounded-input 
                             text-text-primary placeholder-text-muted
                             focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors"
                  >
                    <option value="">Select Business Type</option>
                    {businessTypes.map(type => (
                      <option key={type.businesstypeid} value={type.businesstypeid}>
                        {type.businesstype}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <MapPin size={20} />
                Address Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  name="address1"
                  type="text"
                  label="Address Line 1 (Optional)"
                  placeholder="Street address"
                  value={formData.address1}
                  onChange={handleChange}
                  fullWidth
                  disabled={isLoading}
                />
                <Input
                  name="address2"
                  type="text"
                  label="Address Line 2 (Optional)"
                  placeholder="Apt, suite, unit, etc."
                  value={formData.address2}
                  onChange={handleChange}
                  fullWidth
                  disabled={isLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Globe size={16} />
                      Country *
                    </span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className="w-full px-3 py-2 bg-bg-secondary border border-surface-secondary rounded-input 
                             text-text-primary placeholder-text-muted
                             focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors"
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryid}>
                        {country.countryname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    State/Province *
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={isLoading || !formData.country || loadingGeo}
                    required
                    className="w-full px-3 py-2 bg-bg-secondary border border-surface-secondary rounded-input 
                             text-text-primary placeholder-text-muted
                             focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors"
                  >
                    <option value="">Select State/Province</option>
                    {states.map(state => (
                      <option key={state.stateid} value={state.stateid}>
                        {state.statename}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    City *
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={isLoading || !formData.state || loadingGeo}
                    required
                    className="w-full px-3 py-2 bg-bg-secondary border border-surface-secondary rounded-input 
                             text-text-primary placeholder-text-muted
                             focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors"
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.cityid} value={city.cityid}>
                        {city.cityname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <Input
                  name="postal_code"
                  type="text"
                  label="Postal/ZIP Code (Optional)"
                  placeholder="12345"
                  value={formData.postal_code}
                  onChange={handleChange}
                  fullWidth
                  disabled={isLoading}
                  maxLength={10}
                />
              </div>
            </div>

            {/* Admin User Information */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <User size={20} />
                Admin User Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="first_name"
                  type="text"
                  label="First Name *"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleChange}
                  leftIcon={<User size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                />
                <Input
                  name="last_name"
                  type="text"
                  label="Last Name *"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                  leftIcon={<User size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="mt-4">
                <Input
                  name="email"
                  type="email"
                  label="Email Address *"
                  placeholder="admin@yourcompany.com"
                  value={formData.email}
                  onChange={handleChange}
                  leftIcon={<Mail size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  name="password"
                  type="password"
                  label="Password *"
                  placeholder="Min. 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  leftIcon={<Lock size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                  minLength={8}
                />
                <Input
                  name="confirm_password"
                  type="password"
                  label="Confirm Password *"
                  placeholder="Re-enter password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  leftIcon={<Lock size={18} />}
                  fullWidth
                  disabled={isLoading}
                  required
                  minLength={8}
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-text-muted">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-accent-primary hover:text-accent-secondary transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-text-muted">
          © 2026 SecureView. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
